import { useState } from "react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface UseSMSReturn {
    message: string;
    setMessage: (message: string) => void;
    isGenerating: boolean;
    isSending: boolean;
    error: string | null;
    generateAIMessage: () => Promise<void>;
    sendSMS: () => Promise<boolean>;
    characterCount: number;
    isOverLimit: boolean;
}

export const useSMS = (contactId: number): UseSMSReturn => {
    const [message, setMessage] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const characterCount = message.length;
    const isOverLimit = characterCount > 160;

    const generateAIMessage = async () => {
        setIsGenerating(true);
        setError(null);

        try {
            const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-sms-generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${SUPABASE_KEY}`,
                },
                body: JSON.stringify({ contact_id: contactId }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Failed to generate message");
            }

            setMessage(data.message || "");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to generate AI message");
            console.error("AI generation error:", err);
        } finally {
            setIsGenerating(false);
        }
    };

    const sendSMS = async (): Promise<boolean> => {
        if (!message.trim()) {
            setError("Message cannot be empty");
            return false;
        }

        setIsSending(true);
        setError(null);

        try {
            const response = await fetch(`${SUPABASE_URL}/functions/v1/sms-send`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${SUPABASE_KEY}`,
                },
                body: JSON.stringify({
                    contact_id: contactId,
                    message: message.trim(),
                    ai_generated: false, // Could track if the message was AI-generated
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Failed to send SMS");
            }

            // Clear message on success
            setMessage("");
            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to send SMS");
            console.error("SMS send error:", err);
            return false;
        } finally {
            setIsSending(false);
        }
    };

    return {
        message,
        setMessage,
        isGenerating,
        isSending,
        error,
        generateAIMessage,
        sendSMS,
        characterCount,
        isOverLimit,
    };
};
